import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const contactFormSchema = z.object({
  name: z.string().trim().min(1, "Full name is required").max(100, "Name must be less than 100 characters"),
  phone: z.string().trim().min(1, "Phone number is required").max(20, "Phone must be less than 20 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  address: z.string().trim().min(1, "Property address is required").max(200, "Address must be less than 200 characters"),
  service: z.string().min(1, "Please select a service"),
  timeline: z.string().min(1, "Please select a timeline"),
  details: z.string().trim().max(1000, "Details must be less than 1000 characters").optional(),
  // Honeypot field - should remain empty
  website: z.string().max(0, "Invalid submission").optional(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      address: "",
      service: "",
      timeline: "",
      details: "",
      website: "",
    },
  });

  const onSubmit = async (values: ContactFormValues) => {
    setIsSubmitting(true);

    // Check honeypot - if filled, silently reject
    if (values.website && values.website.length > 0) {
      console.warn("Spam detected: honeypot field filled");
      setIsSubmitting(false);
      toast.error("There was an issue with your submission. Please try again.");
      return;
    }

    try {
      // Save to database
      const { error: dbError } = await supabase.from("contacts").insert({
        name: values.name,
        email: values.email,
        phone: values.phone,
        address: values.address,
        service: values.service,
        timeline: values.timeline,
        message: values.details || null,
      });

      if (dbError) {
        // Check if it's a rate limit error
        if (dbError.message?.includes("row-level security policy")) {
          toast.error("You've already submitted a request recently. Please wait 24 hours or call us directly at (346) 220-0989.");
          setIsSubmitting(false);
          return;
        }
        throw dbError;
      }

      // Send email via edge function
      const { error: emailError } = await supabase.functions.invoke("send-contact-email", {
        body: values,
      });

      if (emailError) {
        console.error("Email sending error:", emailError);
        toast.error("Your message was saved, but we couldn't send a confirmation email.");
      } else {
        toast.success("Thank you! We'll get back to you soon.");
        
        // Track Meta Pixel Lead event
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead', {
            content_name: 'Contact Form Submission',
            content_category: values.service,
            value: values.timeline,
          });
        }
      }

      form.reset();
    } catch (error: any) {
      console.error("Error submitting contact form:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="h-11 bg-white border-input focus-ring-smooth focus-visible:ring-primary transition-all duration-300 hover:border-primary/50 rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Phone Number *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(346) 220-0989" {...field} className="h-11 bg-white border-input focus-ring-smooth focus-visible:ring-primary transition-all duration-300 hover:border-primary/50 rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="h-11 bg-white border-input focus-ring-smooth focus-visible:ring-primary transition-all duration-300 hover:border-primary/50 rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Property Address *</FormLabel>
              <FormControl>
                <Input placeholder="123 Main St, Houston, TX 77001" {...field} className="h-11 bg-white border-input focus-ring-smooth focus-visible:ring-primary transition-all duration-300 hover:border-primary/50 rounded-lg" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">What brings you to us today? *</FormLabel>
              <FormControl>
                <select
                  {...field}
                  className="flex h-11 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-ring-smooth transition-all duration-300 hover:border-primary/50 cursor-pointer"
                >
                  <option value="">Select a service...</option>
                  <option value="general-inspection">General Roof Inspection</option>
                  <option value="leak-damage">Leak or Damage Repair</option>
                  <option value="storm-damage">Storm Damage Assessment</option>
                  <option value="roof-replacement">Full Roof Replacement</option>
                  <option value="insurance-claim">Insurance Claim Help</option>
                  <option value="preventative">Preventative Maintenance</option>
                  <option value="emergency">Emergency Repair</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="timeline"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">When do you need this done?</FormLabel>
              <FormControl>
                <select
                  {...field}
                  className="flex h-11 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-ring-smooth transition-all duration-300 hover:border-primary/50 cursor-pointer"
                >
                  <option value="">Select a timeline...</option>
                  <option value="asap">As soon as possible</option>
                  <option value="week">Within a week</option>
                  <option value="month">Within a month</option>
                  <option value="planning">Just planning ahead</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="details"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Tell us more about your roof (optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional details about your roofing needs..."
                  className="min-h-[100px] bg-white border-input focus-visible:ring-primary focus-ring-smooth transition-all duration-300 hover:border-primary/50 rounded-lg"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Honeypot field - hidden from users, catches bots */}
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input {...field} tabIndex={-1} autoComplete="off" />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-primary text-white hover:bg-primary/90 font-semibold h-12 rounded-lg text-base">
          {isSubmitting ? "Sending..." : "Get My Free Estimate"}
        </Button>
        
        <p className="text-xs text-muted-foreground text-center mt-2">
          Your information is secure. We'll contact you the same day, typically within the same hour.
        </p>
      </form>
    </Form>
  );
}
